// src/routes/AppRoutes.js
import React from "react";
import { Routes, Route, Navigate, useLocation } from "react-router-dom";
import ProtectedRoute from "./ProtectedRoute";
import Role from "../utils/roles";

// Import the page components
import DashboardPage from "../pages/Dashboard/AdminDashboard/Dashboard";
import ControlsHome from "../pages/Controls/ControlsHome";
import EvidenceHome from "../pages/Evidence/EvidenceHome";
import AIEvidenceHome from "../pages/IntegratedEvidence/AIEvidenceHome";
import HowRegoAIWorks from "../pages/IntegratedEvidence/HowRegoAIWorks";
import RiskManagementHome from "../pages/Risks/RiskManagement/pages/RiskManagementHome";
import EmployeeDashboard from "../pages/Dashboard/EmployeeDashboard/EmployeeDashboard";
import Frameworks from "../pages/Settings/Frameworks/Frameworks";
import CompanySettings from "../pages/Settings/CompanySettings/CompanySettings";
import AdminList from "../pages/Settings/AdminFlow";
import SecurityDetails from "../pages/Settings/Frameworks/SecurityDetails/SecurityDetails";
import PeopleMain from "../pages/Organization/PeopleFlow";
import PolicyList from "../pages/Settings/PoliciesNDocs";
import PolicyDetails from "../pages/Settings/PoliciesNDocs/PolicyDetails/PolicyDetails";
import EmployeePolicies from "../pages/Settings/PoliciesNDocs/EmployeePolicies/EmployeePolicies";
import InfraMain from "../pages/Organization/InfraManagement";
import InfraOverview from "../pages/Organization/InfraManagement/InfraOverview/Infra";
import InfraStructureList from "../pages/Organization/InfraManagement/InfrastructureList/InfraStructureList";
import AuditLogs from "../pages/Settings/AuditLogs/AuditLogs";
import AuditList from "../pages/Settings/Audits/pages/AuditList";
import AuditDetails from "../pages/Settings/Audits/pages/AuditDetails";
import AuditorAuditList from "../pages/Auditor/AuditorAuditList";
import AuditorAuditDetails from "../pages/Auditor/AuditorAuditDetails";
import AccessMain from "../pages/Risks/Access";
import SystemDetails from "../pages/Risks/Access/Systems/SystemDetails/SystemDetails";
import VendorMain from "../pages/Vendors";
import VendorDetails from "../pages/Vendors/Detail/VendorDetails";
import ChatUI from "../pages/Chat/Chat";
import Integrations from "../pages/Settings/Integrations/Integrations";
import EmployeePolicyAck from "../pages/Settings/PoliciesNDocs/EmployeePolicies/EmployeePolicyAck";
import useIsDemoInstance from "../pages/Chat/Chat-Hooks/useIsDemoInstance";
import TrainingEmployeePage from "../pages/Training/TrainingPages/TrainingEmployee.js";
import EmployeeAssets from "../pages/Assets/EmployeeAssets/EmployeeAssets";
import TrainingModule from "../pages/Training/TrainingPages/TrainingModule.js";
import TrainingAdminPage from "../pages/Training/TrainingPages/TrainingAdmin.js";
import TrainingMaterialViewer from "../pages/Training/TrainingPages/TrainingMateriaViewer.js";
import TaskHome from "../pages/Task/TaskHome";
import CheckDetailsPage from "../pages/Checks/CheckDetailsPage";
import EvidenceDetailsPage from "../pages/Evidence/EvidenceDetailsPage";
import ControlDetailsPage from "../pages/Settings/Audits/pages/ControlDetailsPage";
import AssetsHome from "../pages/Assets/AssetsHome";
import AgentRemediationSteps from "../pages/AgentRemediation/AgentRemediationSteps";
import CodeSecurityHome from "../pages/Organization/CodeSecurity";
import CodeSecurityBranches from "../pages/Organization/CodeSecurity/RepoBranches";
import CodeSecurityFindings from "../pages/Organization/CodeSecurity/BranchFindings";
import {
  RESTRICTED_HOME_ROUTE,
  isPathAllowedForRestrictedAccess,
  useRestrictedModuleAccess,
} from "../components/AccessControl";

const RedirectLegacyCodeSecurity = () => {
  const location = useLocation();
  const rest = location.pathname.replace(/^\/code-security/, "") || "";
  return (
    <Navigate
      to={`/admin/code-security${rest}${location.search}${location.hash}`}
      replace
    />
  );
};

const AppRoutes = ({
  openSecurityDetails,
  securityDetailsData,
  closeSecurityDetails,
  setBreadcrumbLists,
  isEmployeeMode,
  isAuditorMode,
  dashboardData,
}) => {
  const isDemoInstance = useIsDemoInstance();
  const location = useLocation();
  const isRestrictedAccess = useRestrictedModuleAccess();

  if (
    isRestrictedAccess &&
    !isPathAllowedForRestrictedAccess(location.pathname)
  ) {
    return <Navigate to={RESTRICTED_HOME_ROUTE} replace />;
  }

  return (
    <Routes>
      {/* Dashboard routes based on mode */}
      <Route
        path="/employee/dashboard"
        element={
          isEmployeeMode ? (
            // <EmployeeDashboard dashboardData={dashboardData} />
            <Navigate to="/employee/policies" replace />
          ) : (
            <Navigate to="/admin/dashboard" replace />
          )
        }
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requireRoles={[Role.ADMIN]}>
            <Routes>
              <Route
                path="dashboard"
                element={
                  <DashboardPage openSecurityDetails={openSecurityDetails} />
                }
              />
              <Route path="controls" element={<ControlsHome />} />
              <Route path="evidence" element={<EvidenceHome />} />
              <Route path="integrated-evidences" element={<AIEvidenceHome />} />
              <Route path="regoai" element={<HowRegoAIWorks />} />
              <Route path="policies" element={<PolicyList />} />
              <Route path="risks" element={<RiskManagementHome />} />
              <Route path="access" element={<AccessMain />} />
              <Route path="access/system/:systemId" element={<SystemDetails />} />
              {isDemoInstance && (
                <>
                  <Route path="vendors" element={<VendorMain />} />
                  <Route path="vendors/:vendorId" element={<VendorDetails />} />
                </>
              )}
              <Route path="training" element={<TrainingAdminPage />} />
              <Route path="tasks" element={<TaskHome />} />
              <Route path="audits" element={<AuditList />} />
              <Route path="audits/:id" element={<AuditDetails />} />
              <Route path="audits/:auditId/control/:controlId" element={<ControlDetailsPage />} />
              <Route path="audits/:auditId/evidences-collections/:checkType" element={<EvidenceDetailsPage />} />
              <Route path="assets" element={<AssetsHome />} />
              <Route path="code-security" element={<CodeSecurityHome />} />
              <Route
                path="code-security/:repoId"
                element={<CodeSecurityBranches />}
              />
              <Route
                path="code-security/:repoId/:branchId"
                element={<CodeSecurityFindings />}
              />
              <Route
                path="*"
                element={<Navigate to="/admin/dashboard" replace />}
              />
            </Routes>
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute requireRoles={[Role.ADMIN]}>
            <Routes>
              {/* Superseded by /company-settings. Kept so existing links and
                  bookmarks keep working. */}
              <Route
                path="company-profile"
                element={<Navigate to="/company-settings" replace />}
              />
              <Route path="integrations" element={<Integrations />} />
              <Route path="admins" element={<AdminList />} />
              <Route path="policy/detail/:name" element={<PolicyDetails />} />
              <Route path="people" element={<PeopleMain />} />
              <Route path="infrastructure" element={<InfraMain />} />
              <Route
                path="code-security"
                element={<RedirectLegacyCodeSecurity />}
              />
              <Route
                path="code-security/:repoId"
                element={<RedirectLegacyCodeSecurity />}
              />
              <Route
                path="code-security/:repoId/:branchId"
                element={<RedirectLegacyCodeSecurity />}
              />
              <Route path="checks/:checkType" element={<CheckDetailsPage />} />
              <Route
                path="infrastructure/overview"
                element={<InfraOverview />}
              />
              <Route path="audit-logs" element={<AuditLogs />} />
              
              <Route
                path="infrastructure/overview/:name"
                element={<InfraStructureList />}
              />
              {isDemoInstance && (
                <>
                  <Route path="rego-ai/*" element={<ChatUI />} />
                  <Route path="/assets/*" element={<AssetsHome />} />
                </>
              )}
              <Route
                path="*"
                element={<Navigate to="/admin/dashboard" replace />}
              />
            </Routes>
          </ProtectedRoute>
        }
      />
      {/* Auditor routes */}
      <Route
        path="/auditor/*"
        element={
          <ProtectedRoute requireRoles={[Role.AUDITOR]}>
            <Routes>
              <Route path="audits" element={<AuditorAuditList />} />
              <Route path="audits/:id" element={<AuditorAuditDetails />} />
              <Route path="audits/:auditId/control/:controlId" element={<ControlDetailsPage />} />
              <Route path="audits/:auditId/evidences-collections/:checkType" element={<EvidenceDetailsPage />} />
              <Route
                path="*"
                element={<Navigate to="/auditor/audits" replace />}
              />
            </Routes>
          </ProtectedRoute>
        }
      />

      {/* Company Settings is open to every signed-in role: the "My
          notifications" tab belongs to each user. Admin-only surfaces (the
          About form and the Company notifications tab) are gated inside the
          page itself. */}
      <Route
        path="/company-settings"
        element={
          <ProtectedRoute
            requireRoles={[Role.ADMIN, Role.EMPLOYEE, Role.AUDITOR]}
          >
            <CompanySettings setBreadcrumbLists={setBreadcrumbLists} />
          </ProtectedRoute>
        }
      />

      {/* Employee routes - protected for Employee and Admin */}
      <Route
        path="/employee/policies"
        element={
          <ProtectedRoute requireRoles={[Role.EMPLOYEE, Role.ADMIN]}>
            <EmployeePolicies />
          </ProtectedRoute>
        }
      />
      <Route
        path="/policy/ack/:name"
        element={
          <ProtectedRoute requireRoles={[Role.EMPLOYEE, Role.ADMIN]}>
            <EmployeePolicyAck />
          </ProtectedRoute>
        }
      />
      <Route
        path="/agentRemediationSteps"
        element={
          <ProtectedRoute requireRoles={[Role.EMPLOYEE, Role.ADMIN]}>
            <AgentRemediationSteps />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/training"
        element={
          <ProtectedRoute requireRoles={[Role.EMPLOYEE, Role.ADMIN]}>
            <TrainingEmployeePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/employee/assets"
        element={
          <ProtectedRoute requireRoles={[Role.EMPLOYEE, Role.ADMIN]}>
            <EmployeeAssets />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/:moduleName"
        element={
          <ProtectedRoute requireRoles={[Role.EMPLOYEE, Role.ADMIN]}>
            <TrainingModule />
          </ProtectedRoute>
        }
      />
      <Route
        path="/training/:moduleName/:name"
        element={
          <ProtectedRoute requireRoles={[Role.EMPLOYEE, Role.ADMIN]}>
            <TrainingMaterialViewer />
          </ProtectedRoute>
        }
      />
      {/* Default route based on user mode */}
      <Route
        path="*"
        element={
          isAuditorMode ? (
            <Navigate to="/auditor/audits" replace />
          ) : isEmployeeMode ? (
            <Navigate to="/employee/policies" replace />
          ) : (
            <Navigate to="/admin/dashboard" replace />
          )
        }
      />
    </Routes>
  );
};

export default AppRoutes;
